import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from '@/modules/auth/hooks/useAuth.hook';
import { useGetDefaultPage, useGetMe } from '@linkie/shared';
import { loadKibo, endKiboSession } from '@/core/lib/load-kibo';
import { isAppSurface } from '@/core/utils/is-app-surface';
import { isLoggedIn } from '@/core/utils/is-logged-in';

const useKibo = () => {
  const { state } = useAuth();
  const user = state.user;
  const { pathname } = useLocation();

  // `isPending` stays true while the query is disabled, so it is only meaningful once
  // there is a user to query for.
  const { data, isPending } = useGetDefaultPage(user?._id ?? '', {
    queryConfig: { enabled: !!user?._id },
  });
  const page = data?.data;

  const hasSession = isLoggedIn();

  // `/auth/me` is already in flight from useSyncAuthIdentity, so observing the same query
  // key costs nothing. It is the only thing that can tell us a cookie-bearing visitor will
  // *not* resolve into a user — a 401 on a dead token — which would otherwise hold the
  // anonymous chat back forever on the login page, the one screen it matters most on.
  const { isPending: isIdentityPending } = useGetMe({
    queryConfig: { enabled: hasSession && !user, retry: false },
  });

  // AppLayout never unmounts, so this has to be recomputed per navigation rather than once —
  // passing `pathname` in is what makes a client-side move onto a public page register here.
  const isApp = isAppSurface(pathname);

  const signedInUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!isApp) return;

    if (!user) {
      // A signed-in visitor's identity may still be resolving — the auth store rehydrates
      // out of IndexedDB asynchronously. Injecting now would let the SDK snapshot an
      // anonymous contact, and it never re-reads the config, so hold until we know.
      if (hasSession && isIdentityPending) return;

      if (signedInUserId.current) endKiboSession();
      signedInUserId.current = null;

      // Anonymous on an auth screen: someone who cannot get into their account is exactly
      // who needs support, so the chat loads with no identity attached.
      //
      // Known limitation: signing in from here is a client-side navigation, and the SDK
      // registers its contact once during its own mount with no re-identify entry point.
      // So a visitor who does get in keeps an anonymous chat until their next full load.
      // Re-injecting would leave the first instance's websocket and document.title
      // subscription running, so the only real fix is a hard navigation after login.
      loadKibo();
      return;
    }

    // The SDK reads `window.$kibo` once and never again, so the avatar has to be in hand
    // before the script is injected or support sees a contact with no picture. The query
    // settles either way (success or terminal error), so this cannot strand the widget.
    if (isPending) return;

    const config = loadKibo();

    config.userId = user._id;
    config.name = user.name;
    config.email = user.email;
    config.picture = page?.avatar_url ?? undefined;

    signedInUserId.current = user._id;
  }, [user, page, isPending, isApp, hasSession, isIdentityPending]);
};

export default useKibo;
